<template>
  <div
    :class="[
      'p-carousel p-component',
      {
        'p-carousel-vertical': orientation === 'vertical',
        'p-carousel-horizontal': orientation === 'horizontal',
        'p-carousel-rtl': isRTL,
        [`p-carousel-${size}`]: size !== 'normal'
      }
    ]"
    :aria-label="computedAriaLabel"
    role="region"
  >
    <Carousel
      ref="carouselRef"
      :value="modelValue"
      :numVisible="numVisible"
      :numScroll="numScroll"
      :orientation="orientation"
      :showNavigators="showNavigators"
      :showIndicators="showIndicators"
      :autoplay="autoplay"
      :autoplayInterval="autoplayInterval"
      :circular="circular"
      :responsiveOptions="responsiveOptions"
      :lazy="lazyLoad"
      :touchSwipe="touchSwipe"
      :mouseDrag="mouseDrag"
      :pauseOnHover="pauseOnHover"
      :dataKey="dataKey"
      :pt="ptOptions"
      @page-change="onPageChange"
    >
      <template #item="slotProps">
        <slot
          name="item"
          :item="slotProps.data"
          :index="slotProps.index"
        >
          <div
            class="p-carousel-item"
            :class="{ 'p-carousel-item-active': slotProps.index === currentIndex }"
            role="tabpanel"
            :aria-hidden="slotProps.index !== currentIndex"
            :aria-label="`Item ${slotProps.index + 1} of ${modelValue.length}`"
          >
            {{ String(slotProps.data.content) }}
          </div>
        </slot>
      </template>

      <template v-if="$slots.header" #header>
        <slot name="header" />
      </template>

      <template v-if="$slots.footer" #footer>
        <slot name="footer" />
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Carousel from 'primevue/carousel'
import type { CarouselProps, CarouselEmits, CarouselResponsiveOption, CarouselSize } from './types'
import { useLocalization } from '../../../../composables/useLocalization'

interface IndicatorContext {
  active: boolean;
}

const props = withDefaults(defineProps<CarouselProps>(), {
  numVisible: 1,
  numScroll: 1,
  orientation: 'horizontal',
  showNavigators: true,
  showIndicators: true,
  autoplay: false,
  autoplayInterval: 3000,
  circular: false,
  responsiveOptions: () => [],
  lazyLoad: false,
  touchSwipe: true,
  mouseDrag: false,
  pauseOnHover: true,
  size: 'normal',
  dataKey: undefined,
  ariaLabel: undefined,
  prevButtonAriaLabel: undefined,
  nextButtonAriaLabel: undefined,
  indicatorsAriaLabel: undefined
})

const emit = defineEmits<CarouselEmits>()

const { t, isRTL } = useLocalization()
const carouselRef = ref<InstanceType<typeof Carousel> | null>(null)
const currentIndex = ref(0)

// Computed properties for ARIA labels
const computedAriaLabel = computed(() => props.ariaLabel || t('one.carousel.ariaLabel'))
const computedPrevButtonAriaLabel = computed(() => props.prevButtonAriaLabel || t('one.carousel.prevPage'))
const computedNextButtonAriaLabel = computed(() => props.nextButtonAriaLabel || t('one.carousel.nextPage'))
const computedIndicatorsAriaLabel = computed(() => props.indicatorsAriaLabel || t('one.carousel.pageIndicators'))

// Computed classes
const rootClass = computed(() => [
  'p-carousel-items-container',
  {
    'p-carousel-items-container-vertical': props.orientation === 'vertical',
    'p-carousel-items-container-horizontal': props.orientation === 'horizontal'
  }
])

const contentClass = computed(() => [
  'p-carousel-content',
  {
    'p-carousel-content-vertical': props.orientation === 'vertical',
    'p-carousel-content-horizontal': props.orientation === 'horizontal'
  }
])

const containerClass = computed(() => [
  'p-carousel-container',
  {
    'p-carousel-container-vertical': props.orientation === 'vertical',
    'p-carousel-container-horizontal': props.orientation === 'horizontal'
  }
])

const navigationClass = computed(() => [
  'p-carousel-nav-button',
  {
    'p-carousel-nav-button-vertical': props.orientation === 'vertical',
    'p-carousel-nav-button-horizontal': props.orientation === 'horizontal',
    [`p-carousel-nav-button-${props.size}`]: props.size !== 'normal'
  }
])

const indicatorsClass = computed(() => [
  'p-carousel-indicators',
  {
    'p-carousel-indicators-vertical': props.orientation === 'vertical',
    'p-carousel-indicators-horizontal': props.orientation === 'horizontal',
    [`p-carousel-indicators-${props.size}`]: props.size !== 'normal'
  }
])

const ptOptions = computed(() => ({
  root: { class: rootClass.value },
  content: { class: contentClass.value },
  container: { class: containerClass.value },
  previousButton: {
    class: navigationClass.value,
    'aria-label': computedPrevButtonAriaLabel.value,
    role: 'button'
  },
  nextButton: {
    class: navigationClass.value,
    'aria-label': computedNextButtonAriaLabel.value,
    role: 'button'
  },
  indicators: {
    class: indicatorsClass.value,
    role: 'tablist',
    'aria-label': computedIndicatorsAriaLabel.value
  },
  indicator: {
    role: 'tab',
    getProps: (context: IndicatorContext) => ({
      'aria-selected': context.active ? 'true' : 'false',
      'aria-label': `Page ${context.active ? 'active' : ''}`
    })
  }
}))

// Event handlers
const onPageChange = (event: { page: number }) => {
  currentIndex.value = event.page * props.numVisible
  const startIndex = currentIndex.value
  const endIndex = startIndex + props.numVisible
  const currentItem = props.modelValue[startIndex]

  emit('update:page', event.page)
  emit('page-change', {
    page: event.page,
    first: startIndex,
    rows: props.numVisible,
    pageCount: Math.ceil(props.modelValue.length / props.numVisible)
  })
  emit('update:first', startIndex)
  emit('update:last', endIndex - 1)
  emit('item-change', currentItem)
}
</script>

<style lang="scss">
.p-carousel {
  display: flex;
  flex-direction: column;
  width: 100%;

  &.p-carousel-vertical {
    height: 100%;

    .p-carousel-container {
      flex-direction: column;
    }

    .p-carousel-items-container {
      height: 100%;
    }

    .p-carousel-indicators {
      flex-direction: column;
      padding: 1rem 0;
    }
  }

  &.p-carousel-horizontal {
    .p-carousel-container {
      flex-direction: row;
    }

    .p-carousel-indicators {
      flex-direction: row;
      padding: 0 1rem;
    }
  }

  &.p-carousel-rtl {
    direction: rtl;

    .p-carousel-container {
      flex-direction: row-reverse;
    }

    .p-carousel-indicators {
      flex-direction: row-reverse;
    }

    .p-carousel-nav-button {
      &.p-carousel-prev {
        right: 0;
        left: auto;
      }

      &.p-carousel-next {
        left: 0;
        right: auto;
      }
    }
  }

  // Size variants
  &.p-carousel-small {
    .p-carousel-content {
      padding: 0.5rem;
    }

    .p-carousel-nav-button {
      width: 1.5rem;
      height: 1.5rem;
      font-size: 0.875rem;
    }

    .p-carousel-indicators {
      padding: 0.5rem;

      button {
        width: 0.5rem;
        height: 0.5rem;
      }
    }
  }

  &.p-carousel-large {
    .p-carousel-content {
      padding: 1.5rem;
    }

    .p-carousel-nav-button {
      width: 3rem;
      height: 3rem;
      font-size: 1.5rem;
    }

    .p-carousel-indicators {
      padding: 1.5rem;

      button {
        width: 1rem;
        height: 1rem;
      }
    }
  }

  .p-carousel-content {
    display: flex;
    flex-direction: column;
    padding: 1rem;
  }

  .p-carousel-container {
    display: flex;
    flex: 1;
    position: relative;
  }

  .p-carousel-items-container {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .p-carousel-items-content {
    display: flex;
    transition: transform 0.2s;
  }

  .p-carousel-item {
    flex: 1 0 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    transition: opacity 0.2s;

    &[aria-hidden="true"] {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .p-carousel-nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2rem;
    height: 2rem;
    color: var(--text-color);
    border: 0 none;
    background: transparent;
    border-radius: 50%;
    transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
    cursor: pointer;

    &:hover {
      background: var(--surface-hover);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px var(--focus-ring);
    }

    &:active {
      transform: translateY(-50%) scale(0.95);
    }

    &.p-carousel-prev {
      left: 0;
    }

    &.p-carousel-next {
      right: 0;
    }

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  .p-carousel-indicators {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 0.75rem;
      height: 0.75rem;
      color: var(--text-color-secondary);
      border: 0 none;
      background: var(--surface-d);
      border-radius: 50%;
      transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
      cursor: pointer;

      &:hover {
        background: var(--surface-hover);
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px var(--focus-ring);
      }

      &.p-highlight {
        background: var(--primary-color);
        color: var(--primary-color-text);
      }

      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }

  // High contrast mode support
  @media (forced-colors: active) {
    .p-carousel-nav-button,
    .p-carousel-indicators button {
      border: 1px solid ButtonText;

      &:hover {
        background: Highlight;
        color: HighlightText;
      }

      &:focus {
        outline: 2px solid Highlight;
        outline-offset: 2px;
      }
    }
  }

  // Keyboard navigation
  .p-carousel-nav-button:focus-visible,
  .p-carousel-indicators button:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
}
</style>
